import React from 'react';
import { Card } from '../ui/card';
import { CalendarButton } from '../ui/CalendarButton';
import '../teams/teams.css';
import '../notifications/notifications.css';
import '../../styles/messages.css';

function isCalendarAction(label) {
  return /\bcalendar\b/i.test(String(label || ''));
}

function flattenGroups(groups = []) {
  return groups.flatMap((group) =>
    (group.modules || []).map((module) => ({
      ...module,
      groupTitle: group.title,
      groupDescription: group.description,
    }))
  );
}

function firstModuleKey(family) {
  return flattenGroups(family?.groups || [])[0]?.key || '';
}

function PreviewCard({ card }) {
  if (!card) return null;

  if (card.kind === 'teamStatusCard') {
    return (
      <div className="team-card">
        <div className="card-header-with-icon">
          <span className="card-title">{card.title}</span>
        </div>
        <div className="card-body">
          <div className="availability-status-list">
            {(card.rows || []).map((row) => (
              <div key={`${card.title}-${row.label}`} className="availability-status-item">
                <div className="availability-status-label">
                  <span className="availability-status-icon">{row.icon}</span>
                  <span>{row.label}</span>
                </div>
                <span className={`availability-pill ${row.tone || 'available'}`}>{row.value}</span>
              </div>
            ))}
          </div>
          {card.paragraph ? (
            <p className="text-muted" style={{ marginTop: '0.85rem', marginBottom: 0 }}>
              {card.paragraph}
            </p>
          ) : null}
          {card.actions?.length ? (
            <div className="form-actions" style={{ marginTop: '0.85rem', justifyContent: 'flex-start', flexWrap: 'wrap' }}>
              {card.actions.map((action, index) => (
                isCalendarAction(action) ? (
                  <CalendarButton key={`${card.title}-${action}`} compact type="button">
                    {action}
                  </CalendarButton>
                ) : (
                  <button
                    key={`${card.title}-${action}`}
                    type="button"
                    className={index === 0 ? 'btn btn--primary btn--xs' : 'btn btn--secondary btn--xs'}
                  >
                    {action}
                  </button>
                )
              ))}
            </div>
          ) : null}
        </div>
      </div>
    );
  }

  if (card.kind === 'teamListCard') {
    return (
      <div className="team-card">
        <div className="card-header-with-icon">
          <span className="card-title">{card.title}</span>
        </div>
        <div className="card-body">
          {card.summary ? (
            <p className="text-muted" style={{ marginBottom: '0.75rem' }}>
              {card.summary}
            </p>
          ) : null}
          <div className="marketing-team-list">
            {(card.rows || []).map((row) => (
              <div key={`${card.title}-${row.title}-${row.meta || ''}`} className="marketing-team-list__item">
                <div className="marketing-team-list__main">
                  <div className="marketing-team-list__title">{row.title}</div>
                  {row.meta ? <div className="marketing-team-list__meta">{row.meta}</div> : null}
                </div>
                {row.badge ? (
                  <span className="marketing-team-list__badge">
                    {row.badge}
                  </span>
                ) : null}
              </div>
            ))}
          </div>
          {card.footer ? (
            <p className="text-muted" style={{ marginTop: '0.75rem', marginBottom: 0, fontSize: '0.9rem' }}>
              {card.footer}
            </p>
          ) : null}
        </div>
      </div>
    );
  }

  if (card.kind === 'eventListCard') {
    return (
      <div className="team-card">
        <div className="card-header-with-icon">
          <span className="card-title">{card.title}</span>
        </div>
        <div className="card-body">
          <ul className="text-sm text-gray-700" style={{ paddingLeft: '1rem', marginTop: 0 }}>
            {(card.rows || []).map((row) => (
              <li key={`${card.title}-${row.title}`} className="mb-2">
                <div>
                  <strong>{row.title}</strong>
                  <br />
                  {row.meta}
                </div>
              </li>
            ))}
          </ul>
        </div>
      </div>
    );
  }

  if (card.kind === 'dashboardCard') {
    return (
      <Card title={card.title} className="dashboard-card marketing-dashboard-preview">
        {card.rows?.length ? (
          <ul style={{ paddingLeft: '1rem', marginTop: '0.5rem' }}>
            {card.rows.map((row) => (
              <li key={`${card.title}-${row.title}`}>
                {row.title} – {row.meta}
              </li>
            ))}
          </ul>
        ) : (
          <p className="text-muted">No items yet.</p>
        )}
        {card.buttonLabel ? (
          <div className="form-actions" style={{ marginTop: '1rem', justifyContent: 'flex-start' }}>
            {isCalendarAction(card.buttonLabel) ? (
              <CalendarButton type="button">{card.buttonLabel}</CalendarButton>
            ) : (
              <button type="button" className="button button-secondary">
                {card.buttonLabel}
              </button>
            )}
          </div>
        ) : null}
      </Card>
    );
  }

  if (card.kind === 'documentsCard') {
    return (
      <div className="team-card">
        <div className="card-header-with-icon">
          <span className="card-title">{card.title}</span>
        </div>
        <div className="card-body">
          <div style={{ display: 'grid', gap: '0.9rem' }}>
            {(card.rows || []).map((row) => (
              <div
                key={`${card.title}-${row.title}`}
                style={{
                  border: '1px solid rgba(15, 23, 42, 0.08)',
                  borderRadius: '14px',
                  padding: '0.9rem 1rem',
                  background: 'rgba(255, 255, 255, 0.8)',
                }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', gap: '0.75rem', flexWrap: 'wrap' }}>
                  <div>
                    <div style={{ fontWeight: 700 }}>{row.title}</div>
                    <p className="text-muted" style={{ marginTop: '0.35rem', marginBottom: 0 }}>
                      {row.meta}
                    </p>
                  </div>
                  <span
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      padding: '0.15rem 0.5rem',
                      borderRadius: '999px',
                      fontSize: '0.75rem',
                      fontWeight: 600,
                      background: 'rgba(15, 23, 42, 0.08)',
                      color: '#334155',
                    }}
                  >
                    {row.badge}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (card.kind === 'publicLinksCard') {
    return (
      <div className="team-card">
        <div className="card-header-with-icon">
          <span className="card-title">{card.title}</span>
        </div>
        <div className="card-body">
          <div className="marketing-team-list">
            {(card.rows || []).map((row) => (
              <div key={`${card.title}-${row.label}`} className="marketing-team-list__item">
                <div className="marketing-team-list__main">
                  <div className="marketing-team-list__title">
                    <span style={{ marginRight: 8 }}>{row.icon}</span>
                    {row.label}
                  </div>
                  <div className="marketing-team-list__meta">{row.url}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (card.kind === 'brandingCard') {
    return (
      <div className="team-card">
        <div className="card-header-with-icon">
          <span className="card-title">{card.title}</span>
        </div>
        <div className="card-body">
          {card.logoLabel ? (
            <div className="brand-logo-preview" style={{ display: 'grid', placeItems: 'center', fontWeight: 700 }}>
              {card.logoLabel}
            </div>
          ) : null}
          <ul className="branding-list" style={{ marginTop: card.logoLabel ? '0.75rem' : 0 }}>
            {(card.rows || []).map((row) => (
              <li key={`${card.title}-${row.label}`} className="branding-item">
                <span className="branding-swatch" style={{ backgroundColor: row.value }} />
                <span className="branding-key">{row.label}</span>
                <span className="branding-value" style={{ color: row.value }}>
                  {row.value}
                </span>
              </li>
            ))}
          </ul>
        </div>
      </div>
    );
  }

  if (card.kind === 'joinFormCard') {
    return (
      <Card title={card.title} className="marketing-form-preview">
        {card.enabledLabel ? <p className="text-muted">{card.enabledLabel}</p> : null}
        <div className="marketing-question-list">
          {(card.questions || []).map((question) => (
            <div key={`${card.title}-${question}`} className="marketing-question-list__item">
              <div className="marketing-question-list__label">{question}</div>
              <div className="marketing-question-list__input" />
            </div>
          ))}
        </div>
      </Card>
    );
  }

  if (card.kind === 'profileFieldsCard') {
    return (
      <div className="team-card">
        <div className="card-header-with-icon">
          <span className="card-title">{card.title}</span>
        </div>
        <div className="card-body">
          {card.summary ? (
            <p className="text-muted" style={{ marginBottom: '0.75rem' }}>
              {card.summary}
            </p>
          ) : null}
          <div className="marketing-profile-list">
            {(card.rows || []).map((row) => (
              <div key={`${card.title}-${row.label}`} className="marketing-profile-list__item">
                <div className="marketing-profile-list__label">{row.label}</div>
                <div className="marketing-profile-list__value">{row.value}</div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (card.kind === 'paymentCard') {
    return (
      <Card title={card.title} className="marketing-payment-preview">
        <div className="marketing-payment-rows">
          {(card.rows || []).map((row) => (
            <div
              key={`${card.title}-${row.label}`}
              className={`marketing-payment-row ${row.total ? 'is-total' : ''} ${row.emphasis ? `is-${row.emphasis}` : ''}`}
            >
              <span>{row.label}</span>
              <strong>{row.value}</strong>
            </div>
          ))}
        </div>
        {card.note ? <p className="text-muted" style={{ marginTop: '0.75rem' }}>{card.note}</p> : null}
      </Card>
    );
  }

  if (card.kind === 'notificationsCard') {
    return (
      <Card title={card.title} className="dashboard-card marketing-notification-preview">
        <ul className="notifications-list">
          {(card.rows || []).map((row) => (
            <li key={`${card.title}-${row.message}`} className="notification-item">
              <div className="notification-content">
                <p>
                  <span className="notification-link">{row.message}</span>
                </p>
                <span className="notification-meta">{row.meta}</span>
              </div>
              <button type="button" className="delete-btn" aria-label="Delete notification">
                ×
              </button>
            </li>
          ))}
        </ul>
      </Card>
    );
  }

  if (card.kind === 'messageThreadCard') {
    return (
      <div className="team-card marketing-message-preview-card">
        <div className="card-header-with-icon">
          <span className="card-title">{card.title}</span>
        </div>
        <div className="card-body marketing-message-preview">
          <div className="marketing-message-preview__layout">
          <div className="messages-sidebar">
            <div className="sidebar-header">
              <strong>Threads</strong>
            </div>
            <ul className="thread-list">
              {(card.sidebarItems || []).map((item) => (
                <li
                  key={`${card.title}-${item.title}-${item.meta}`}
                  className={`thread-list-item ${item.active ? 'active' : ''}`}
                >
                  <div style={{ fontWeight: 600 }}>
                    {item.title}
                    {item.broadcast ? <span className="chat-thread-broadcast-pill">Broadcast</span> : null}
                  </div>
                  <div className="last-message">{item.meta}</div>
                </li>
              ))}
            </ul>
          </div>
          <div className="messages-chat">
            <div className="chat-header">
              {card.threadTitle}
              {card.threadBadge ? <span className="chat-broadcast-badge" style={{ marginLeft: 8 }}>{card.threadBadge}</span> : null}
            </div>
            <div className="chat-messages">
              {(card.messages || []).map((message, index) => (
                <div
                  key={`${card.title}-${index}`}
                  className={`chat-message ${message.author}`}
                >
                  {message.badge ? (
                    <div className="chat-bubble-header">
                      <span className="chat-broadcast-badge">{message.badge}</span>
                    </div>
                  ) : null}
                  <div className={`chat-bubble ${message.author}`}>
                    {message.text}
                  </div>
                </div>
              ))}
            </div>
            <div className="chat-input">
              <input type="text" value={card.inputPlaceholder || ''} readOnly />
              <button type="button">Send</button>
            </div>
          </div>
        </div>
        </div>
      </div>
    );
  }

  if (card.kind === 'boardOverviewCard') {
    return (
      <div className="team-card marketing-board-overview-card">
        <div className="card-header-with-icon">
          <span className="card-title">{card.title}</span>
        </div>
        <div className="card-body">
          {card.summary ? (
            <div className="marketing-board-overview-card__summary">
              <span>{card.summary}</span>
              {card.actionLabel ? (
                <button type="button" className="btn btn--primary btn--xs">
                  {card.actionLabel}
                </button>
              ) : null}
            </div>
          ) : null}

          {card.reviewCases?.length ? (
            <>
              <div className="text-muted small marketing-board-overview-card__section-label">Review cases</div>
              <div className="marketing-team-list">
                {card.reviewCases.map((row) => (
                  <div key={`${card.title}-review-${row.title}-${row.meta}`} className="marketing-team-list__item">
                    <div className="marketing-team-list__main">
                      <div className="marketing-team-list__title">{row.title}</div>
                      <div className="marketing-team-list__meta">{row.meta}</div>
                    </div>
                    {row.badge ? <span className="marketing-team-list__badge">{row.badge}</span> : null}
                  </div>
                ))}
              </div>
            </>
          ) : null}

          <div className="text-muted small marketing-board-overview-card__section-label">Recent boards</div>
          <div className="marketing-team-list">
            {(card.rows || []).map((row) => (
              <div key={`${card.title}-${row.title}-${row.meta}`} className="marketing-team-list__item">
                <div className="marketing-team-list__main">
                  <div className="marketing-team-list__title">{row.title}</div>
                  <div className="marketing-team-list__meta">{row.meta}</div>
                </div>
                {row.badge ? <span className="marketing-team-list__badge">{row.badge}</span> : null}
              </div>
            ))}
          </div>
          {card.footer ? <p className="text-muted marketing-board-overview-card__footer">{card.footer}</p> : null}
        </div>
      </div>
    );
  }

  if (card.kind === 'boardThreadCard') {
    return (
      <div className="team-card marketing-board-thread-card">
        <div className="card-header-with-icon">
          <span className="card-title">{card.title}</span>
        </div>
        <div className="card-body">
          <article className="marketing-board-topic">
            <div className="marketing-board-topic__header">
              <div>
                <h5>{card.topicTitle}</h5>
                {card.topicMeta ? <p>{card.topicMeta}</p> : null}
              </div>
              {card.attachments?.length ? (
                <div className="marketing-board-topic__attachments">
                  {card.attachments.map((attachment) => (
                    <span key={`${card.title}-${attachment}`}>{attachment}</span>
                  ))}
                </div>
              ) : null}
            </div>
            {card.body ? <p className="marketing-board-topic__body">{card.body}</p> : null}
            {card.reactions?.length ? (
              <div className="marketing-board-reactions">
                {card.reactions.map((reaction) => (
                  <span
                    key={`${card.title}-${reaction.emoji}`}
                    className={`marketing-board-reaction ${reaction.active ? 'is-active' : ''}`}
                  >
                    {reaction.emoji} {reaction.count}
                  </span>
                ))}
              </div>
            ) : null}
          </article>
          <div className="marketing-board-reply-list">
            {(card.replies || []).map((reply) => (
              <div key={`${card.title}-${reply.author}-${reply.meta}`} className="marketing-board-reply">
                <div className="marketing-board-reply__meta">
                  <strong>{reply.author}</strong>
                  <span>{reply.meta}</span>
                </div>
                <p>{reply.text}</p>
              </div>
            ))}
          </div>
          <div className="marketing-board-composer">
            <div>Write a reply...</div>
            <button type="button" className="btn btn--primary btn--xs">Post Reply</button>
          </div>
          {card.footer ? <p className="text-muted marketing-board-overview-card__footer">{card.footer}</p> : null}
        </div>
      </div>
    );
  }

  if (card.kind === 'dutyRosterCard') {
    return (
      <div className="team-card marketing-duty-roster-card">
        <div className="card-header-with-icon">
          <span className="card-title">{card.title}</span>
        </div>
        <div className="card-body">
          <button type="button" className="marketing-duty-roster-card__primary">
            <span>{card.eyebrow}</span>
            <strong>{card.titleText}</strong>
            <em>{card.meta}</em>
            {card.action ? <b>{card.action}</b> : null}
          </button>
          <div className="marketing-duty-roster-card__stats">
            {(card.stats || []).map((stat) => (
              <button
                key={`${card.title}-${stat.label}`}
                type="button"
                className={`marketing-duty-roster-card__stat marketing-duty-roster-card__stat--${stat.tone || 'ok'}`}
              >
                <span>{stat.label}</span>
                <strong>{stat.value}</strong>
              </button>
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (card.kind === 'mooringMapCard') {
    return (
      <div className="team-card marketing-mooring-map-card">
        <div className="card-header-with-icon">
          <span className="card-title">{card.title}</span>
        </div>
        <div className="card-body">
          <div className="marketing-mooring-map-card__topbar">
            <strong>{card.area}</strong>
            <button type="button" className="btn btn--secondary btn--xs">Open map</button>
          </div>
          <div className="marketing-mooring-map-card__stats">
            {(card.summary || []).map((item) => (
              <div key={`${card.title}-${item.label}`}>
                <span>{item.label}</span>
                <strong>{item.value}</strong>
              </div>
            ))}
          </div>
          <div className="marketing-mooring-map-card__layout">
            <div className="marketing-mooring-map-card__grid" aria-label="Mooring spaces">
              {(card.spaces || []).map((space) => (
                <button
                  key={`${card.title}-${space.label}`}
                  type="button"
                  className={`marketing-mooring-space marketing-mooring-space--${space.status || 'vacant'}`}
                >
                  <strong>{space.label}</strong>
                  <span>{space.status === 'mine' ? 'Mine' : space.status}</span>
                </button>
              ))}
            </div>
            <aside className="marketing-mooring-map-card__detail">
              <h5>{card.detailTitle}</h5>
              {(card.detailRows || []).map((row) => (
                <span key={`${card.title}-${row}`}>{row}</span>
              ))}
              <button type="button" className="btn btn--primary btn--xs">Request this space</button>
            </aside>
          </div>
        </div>
      </div>
    );
  }

  if (card.kind === 'inboxBroadcastCard') {
    return (
      <div className="team-card">
        <div className="card-header-with-icon">
          <span className="card-title">{card.title}</span>
        </div>
        <div className="card-body">
          {card.summary ? (
            <div className="text-muted" style={{ fontSize: '0.9rem' }}>
              {card.summary}
            </div>
          ) : null}
          {card.actionLabel ? (
            <div className="form-actions" style={{ justifyContent: 'flex-start', marginTop: '0.75rem' }}>
              <button type="button" className="btn btn--primary">
                {card.actionLabel}
              </button>
            </div>
          ) : null}
          <div className="inbox-broadcast-list">
            {(card.rows || []).map((row) => (
              <button
                key={`${card.title}-${row.title}-${row.meta}`}
                type="button"
                className="inbox-broadcast-row"
              >
                <div className="inbox-broadcast-row__main">
                  <div className="inbox-broadcast-row__title">{row.title}</div>
                  <div className="text-muted inbox-broadcast-row__meta">{row.meta}</div>
                </div>
                <div className="inbox-broadcast-row__stat">
                  <span>Audience</span>
                  <strong>{row.audience}</strong>
                </div>
                <div className="inbox-broadcast-row__stat">
                  <span>Status</span>
                  <strong>{row.status}</strong>
                </div>
              </button>
            ))}
          </div>
          {card.footer ? (
            <p className="text-muted inbox-broadcast-help">{card.footer}</p>
          ) : null}
        </div>
      </div>
    );
  }

  if (card.kind === 'emailCampaignsCard') {
    return (
      <div className="team-card">
        <div className="card-header-with-icon">
          <span className="card-title">{card.title}</span>
        </div>
        <div className="card-body">
          {card.summary ? (
            <div className="text-muted" style={{ fontSize: '0.9rem', marginBottom: '0.75rem' }}>
              {card.summary}
            </div>
          ) : null}
          <div className="marketing-email-campaign-list">
            {(card.rows || []).map((row) => (
              <button
                key={`${card.title}-${row.title}`}
                type="button"
                className="marketing-email-campaign-row"
              >
                <div>
                  <div style={{ fontWeight: 600 }}>{row.title}</div>
                  <div className="text-muted" style={{ fontSize: '0.9rem' }}>{row.meta}</div>
                </div>
                <div>{row.delivered}</div>
                <div>{row.opened}</div>
                <div>{row.clicked}</div>
                <div>{row.bounced}</div>
              </button>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return null;
}

export default function ModuleExplorer({
  families = [],
  initialFamilyKey = '',
  initialSelectedKeys = {},
  title = 'Explore the features',
  description = '',
}) {
  const fallbackFamilyKey = initialFamilyKey || families[0]?.key || '';
  const [activeFamilyKey, setActiveFamilyKey] = React.useState(fallbackFamilyKey);
  const [selectedKeysByFamily, setSelectedKeysByFamily] = React.useState(() =>
    Object.fromEntries(
      (families || []).map((family) => [
        family.key,
        initialSelectedKeys[family.key] || firstModuleKey(family),
      ])
    )
  );

  React.useEffect(() => {
    if (!families.some((family) => family.key === activeFamilyKey)) {
      setActiveFamilyKey(fallbackFamilyKey);
    }
  }, [activeFamilyKey, families, fallbackFamilyKey]);

  React.useEffect(() => {
    setSelectedKeysByFamily((current) => {
      const next = { ...current };
      families.forEach((family) => {
        const moduleKeys = new Set(flattenGroups(family.groups).map((module) => module.key));
        if (!moduleKeys.has(next[family.key])) {
          next[family.key] = initialSelectedKeys[family.key] || firstModuleKey(family);
        }
      });
      return next;
    });
  }, [families, initialSelectedKeys]);

  const activeFamily = families.find((family) => family.key === activeFamilyKey) || families[0] || null;
  const activeModules = React.useMemo(
    () => flattenGroups(activeFamily?.groups || []),
    [activeFamily]
  );
  const selectedKey = selectedKeysByFamily[activeFamily?.key] || activeModules[0]?.key || '';
  const selectedModule =
    activeModules.find((module) => module.key === selectedKey) ||
    activeModules[0] ||
    null;

  if (!activeFamily || !selectedModule) return null;

  const handleFamilyChange = (familyKey) => {
    setActiveFamilyKey(familyKey);
    setSelectedKeysByFamily((current) => {
      const family = families.find((item) => item.key === familyKey);
      const nextSelected = current[familyKey] || initialSelectedKeys[familyKey] || firstModuleKey(family);
      return {
        ...current,
        [familyKey]: nextSelected,
      };
    });
  };

  return (
    <section className="marketing-module-browser">
      <div className="marketing-module-browser__header">
        <h3 className="marketing-heading text-3xl font-semibold">{title}</h3>
        {description ? <p className="marketing-lede">{description}</p> : null}
      </div>

      <div className="marketing-feature-family-switch">
        {families.map((family) => (
          <button
            key={family.key}
            type="button"
            className={`marketing-feature-family-switch__button ${activeFamily.key === family.key ? 'is-active' : ''}`}
            onClick={() => handleFamilyChange(family.key)}
          >
            <span className="marketing-feature-family-switch__label">{family.label}</span>
            <span className="marketing-feature-family-switch__meta">{family.description}</span>
          </button>
        ))}
      </div>

      <div className="marketing-module-browser__layout">
        <div className="marketing-module-browser__nav">
          {activeFamily.groups.map((group) => (
            <div key={group.key} className="marketing-module-browser__group">
              <div className="marketing-module-browser__group-title">{group.title}</div>
              <div className="marketing-module-browser__group-copy">{group.description}</div>
              <div className="marketing-module-browser__button-list">
                {(group.modules || []).map((module) => (
                  <button
                    key={module.key}
                    type="button"
                    className={`marketing-module-button ${selectedModule.key === module.key ? 'is-active' : ''}`}
                    onClick={() =>
                      setSelectedKeysByFamily((current) => ({
                        ...current,
                        [activeFamily.key]: module.key,
                      }))
                    }
                  >
                    <span className="marketing-module-button__label">{module.label}</span>
                    <span className="marketing-module-button__meta">{module.description}</span>
                  </button>
                ))}
              </div>
            </div>
          ))}
        </div>

        <div className="marketing-module-browser__detail">
          <div className="marketing-module-browser__badge">{selectedModule.groupTitle}</div>
          <h4 className="marketing-heading text-3xl font-semibold">{selectedModule.label}</h4>
          <p className="marketing-module-browser__description">{selectedModule.description}</p>

          <div className="marketing-module-browser__surface">
            <div className="marketing-module-browser__surface-title">What it covers</div>
            <ul className="bullet-list">
              {(selectedModule.highlights || []).map((highlight) => (
                <li key={highlight}>{highlight}</li>
              ))}
            </ul>
          </div>

          <div className="marketing-lifecycle-list">
            {(selectedModule.stages || []).map((stage, index) => (
              <section key={stage.key} className="marketing-lifecycle-stage">
                <div className="marketing-lifecycle-stage__header">
                  <div className="marketing-lifecycle-stage__index">{index + 1}</div>
                  <div>
                    <div className="marketing-lifecycle-stage__title">{stage.title}</div>
                    <p className="marketing-lifecycle-stage__description">{stage.description}</p>
                  </div>
                </div>

                <div
                  className={`marketing-stage-card-grid ${
                    (stage.cards || []).length === 1 ? 'marketing-stage-card-grid--single' : ''
                  }`}
                >
                  {(stage.cards || []).map((card) => (
                    <div
                      key={`${stage.key}-${card.title}`}
                      className={`marketing-stage-card-grid__item ${
                        ['messageThreadCard', 'boardThreadCard', 'emailCampaignsCard'].includes(card.kind) ? 'is-wide' : ''
                      }`}
                    >
                      <PreviewCard card={card} />
                    </div>
                  ))}
                </div>
              </section>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
